import React, {
  useMemo,
  useState,
} from 'react';
import isFunction from 'lodash/isFunction';
import clsx from 'clsx';
import {
  useForm,
} from 'react-hook-form';
import {
  Props,
  TemplateForm,
} from 'views/EMR/components/VisitNoteTemplateDialog';
import Controller from 'core/components/FormController';
import ToolTip from 'core/components/Tooltip';
import {
  useMixpanelTrack,
  useNotify,
} from 'core/hooks';
import {
  normalizeContent,
  serializeContent,
} from 'views/EMR/Settings/helper';
import {
  joinSectionTemplateContents,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import Button from 'pages/Dashboard/components/Button';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useTemplateHooks,
  useGetSectionTemplateContents,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  MasterTemplateDTO,
} from 'dtos';
import {
  notEmptyString,
} from '@populateui/utils';
import TemplateSelect from 'pages/Dashboard/components/TemplateSelect';
import DraggableList from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/DraggableList';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import TemplatePreviewDialog from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/TemplatePreviewDialog';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';

const sectionLabels: Record<string, string> = {
  [VisitNoteSOAPSectionNames.AdditionalHPITemplate]: 'Subjective',
  [VisitNoteSOAPSectionNames.PhysicalExamTemplate]: 'Physical Exam',
  [VisitNoteSOAPSectionNames.RadiologyTemplate]: 'Review of Imaging',
  [VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate]: 'Additional Assessment',
  [VisitNoteSOAPSectionNames.PlanTemplate]: 'Plan',
};

export default function ExistingTemplateForm({
  section,
  onClose,
  onSelect,
  content,
  actions,
}: Props) {
  const mixPanelTrack = useMixpanelTrack();
  const actionSet = new Set(actions);
  const notify = useNotify();
  const userInfo = useUserInfo();
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const previewSections = useMemo(() => [{
    key: section ?? 'section',
    label: sectionLabels[section ?? ''] ?? 'Section',
    currentContent: content as string,
    templateContent: previewContent,
  }], [content, previewContent, section]);
  const {
    useGetByName,
    useSave,
  } = useTemplateHooks(section);
  const hasSection = notEmptyString(section);
  const usesBulkSectionTemplates = hasSection
    && isFunction(onSelect)
    && actionSet.has('use');

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { isSubmitting, isValid },
  } = useForm<TemplateForm>({
    mode: 'onChange',
    defaultValues: { templates: [] },
  });
  const selectedTemplate = watch('template');
  const selectedTemplates = watch('templates') ?? [];
  const { data: selectedContent, isFetching } = useGetByName(selectedTemplate?.templateName ?? '', section ?? '');
  const { mutateAsync: saveTemplate } = useSave();
  const {
    mutateAsync: getSectionTemplateContents,
    isPending: isFetchingSelectedTemplates,
  } = useGetSectionTemplateContents();

  const selectedTemplateForUpdate = useMemo(() => {
    const bulkTemplateForUpdate = selectedTemplates.length === 1
      ? selectedTemplates[0]
      : undefined;
    return usesBulkSectionTemplates ? bulkTemplateForUpdate : selectedTemplate;
  }, [selectedTemplate, selectedTemplates, usesBulkSectionTemplates]);

  const selectedTemplateNames = useMemo(() => (
    selectedTemplates
      .map(({ templateName }) => templateName ?? '')
      .filter(notEmptyString)
  ), [selectedTemplates]);

  const removeSelectedTemplate = (templateName: string | null | undefined) => {
    setValue(
      'templates',
      selectedTemplates.filter((template) => template.templateName !== templateName),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const getSelectedContent = async () => {
    if (usesBulkSectionTemplates) {
      const contents = await getSectionTemplateContents({
        templateNames: selectedTemplateNames,
        sectionName: section ?? '',
      });
      return joinSectionTemplateContents(contents);
    }
    return normalizeContent(selectedContent as string);
  };

  const onUse = async () => {
    try {
      onSelect?.(await getSelectedContent());
      onClose();
    } catch {
      // API errors are displayed by the global error handler.
    }
  };

  const onPreview = async () => {
    try {
      setPreviewContent(await getSelectedContent());
    } catch {
      // API errors are displayed by the global error handler.
    }
  };

  const onUpdate = async (formData: TemplateForm) => {
    try {
      const payload = hasSection ? {
        sectionName: section,
        content: serializeContent(content as string),
      } : (content ?? {}) as MasterTemplateDTO;
      await saveTemplate({
        ...payload,
        ...(selectedTemplateForUpdate ?? formData.template),
      });
      notify({
        message: 'Template Has Been Updated!',
        variant: 'success',
      });
      mixPanelTrack('UpdatedVisitNoteTemplate');
      onClose();
    } finally {
      reset(formData, { keepIsSubmitted: false });
    }
  };

  const isUseDisabled = usesBulkSectionTemplates
    ? selectedTemplateNames.length === 0 || isFetchingSelectedTemplates
    : !isValid || isSubmitting || isFetching;

  const isUpdateDisabled = usesBulkSectionTemplates
    ? selectedTemplates.length !== 1
      || selectedTemplateForUpdate?.userId !== userInfo?.user?.userId
      || isSubmitting
    : !isValid
      || isSubmitting
      || selectedTemplateForUpdate?.userId !== userInfo?.user?.userId;

  let updateTooltipTitle: string | undefined;
  if (usesBulkSectionTemplates && selectedTemplates.length !== 1) {
    updateTooltipTitle = 'Select one template to update.';
  } else if (selectedTemplateForUpdate?.userId !== userInfo?.user?.userId) {
    updateTooltipTitle = 'You are not allowed to update templates that are created by another user.';
  }

  return (
    <div className="flex h-full min-h-0 flex-col justify-between gap-2 template-select populate-form">
      {usesBulkSectionTemplates ? (
        <Controller
          name="templates"
          control={control}
          render={({ field }) => (
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
              <Wrapper
                id="template-name"
                label="Available templates"
                className="min-h-0"
                containerClassName="!flex-col min-h-0 flex-1"
              >
                <TemplateSelect
                  inline
                  multiple
                  section={section}
                  selectedTemplates={field.value ?? []}
                  onMultipleChange={field.onChange}
                />
              </Wrapper>
              <div className="flex min-h-0 flex-col">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-semibold">Template order</span>
                  {selectedTemplates.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {selectedTemplates.length}
                        {' '}
                        selected
                      </span>
                      <Button
                        variant="text"
                        color="inherit"
                        className="!min-w-0 !rounded !px-2 !py-0.5 !text-xs !font-medium !text-gray-600 hover:!bg-gray-100 hover:!text-gray-900"
                        onClick={() => { field.onChange([]); }}
                      >
                        Clear All
                      </Button>
                    </div>
                  )}
                </div>
                <p className="mb-2 text-xs text-gray-500">
                  Drag templates into the order they should appear in this section.
                </p>
                <div
                  className={clsx(
                    'min-h-0 flex-1 overflow-y-auto rounded border border-gray-200 bg-gray-50',
                    {
                      'p-1.5': selectedTemplates.length === 0,
                      'px-2 pb-2': selectedTemplates.length > 0,
                    },
                  )}
                >
                  {selectedTemplates.length === 0 ? (
                    <div className="flex h-full min-h-40 items-center justify-center rounded border border-dashed border-gray-300 px-6 text-center text-xs text-gray-500">
                      Select templates from the list to arrange them here.
                    </div>
                  ) : (
                    <DraggableList
                      items={selectedTemplates}
                      getValue={(item) => item.templateName ?? ''}
                      itemSpacing={8}
                      onChange={field.onChange}
                      renderOption={(item) => (
                        <div className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 shadow-sm">
                          <DragIndicatorIcon className="!h-5 !w-5 shrink-0 text-gray-400" />
                          <span className="min-w-0 flex-1 truncate text-xs">
                            {item.templateName}
                          </span>
                          <IconButton
                            aria-label={`Remove ${item.templateName}`}
                            className="!-my-1 !p-1"
                            size="small"
                            onClick={() => { removeSelectedTemplate(item.templateName); }}
                          >
                            <CloseIcon className="!h-4 !w-4 text-gray-500" />
                          </IconButton>
                        </div>
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        />
      ) : (
        <Controller
          name="template"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { invalid } }) => (
            <Wrapper
              id="template-name"
              label="Template"
              required
              error={invalid}
              containerClassName="!flex-col flex-1"
            >
              <TemplateSelect
                {...field}
                section={section}
              />
            </Wrapper>
          )}
        />
      )}
      <div className="flex shrink-0 justify-between">
        <Button
          className="nw-template-cancel-button"
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Cancel
        </Button>
        <div className="flex gap-2">
          {isFunction(onSelect) && actionSet.has('use') && (
            <>
              <Button
                variant="outlined"
                type="button"
                disabled={isUseDisabled}
                onClick={handleSubmit(onPreview)}
              >
                Preview
              </Button>
              <ToolTip
                title={isFetching || isFetchingSelectedTemplates
                  ? 'Fetching content of the selected templates'
                  : ''}
                hide={!isFetching && !isFetchingSelectedTemplates}
              >
                <div>
                  <Button
                    className="nw-template-save-button"
                    color="primary"
                    variant="contained"
                    type="button"
                    disabled={isUseDisabled}
                    onClick={handleSubmit(onUse)}
                  >
                    {usesBulkSectionTemplates
                      ? `Use ${selectedTemplates.length} Selected`
                      : 'Use Selected'}
                  </Button>
                </div>
              </ToolTip>
            </>
          )}
          {actionSet.has('update') && (
          <ToolTip
            title={updateTooltipTitle}
            hide={!updateTooltipTitle}
          >
            <div>
              <Button
                className="nw-template-save-button"
                color="primary"
                variant="contained"
                type="button"
                disabled={isUpdateDisabled}
                onClick={handleSubmit(onUpdate)}
              >
                Update Selected
              </Button>
            </div>
          </ToolTip>
          )}
        </div>
      </div>
      {previewContent !== null && (
        <TemplatePreviewDialog
          sections={previewSections}
          isApplying={isFetchingSelectedTemplates}
          applyLabel={usesBulkSectionTemplates
            ? `Use ${selectedTemplates.length} Selected`
            : 'Use Selected'}
          onClose={() => { setPreviewContent(null); }}
          onApply={() => {
            onSelect?.(previewContent);
            onClose();
          }}
        />
      )}
    </div>
  );
}
